import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { MasterParcoursEngine } from '../engine/MasterParcoursEngineCore';
import { parcoursService } from '../services/parcoursService';

export const usePesaParcours = create(
  devtools((set, get) => {
    // Exposition pour l'Engine (ton pattern fétiche)
    if (typeof window !== 'undefined') {
      window.__PARCOURS_STORE__ = { setState: set };
      window.__PARCOURS_GET_STATE__ = get;
    } 

    return {
      // === L'ENGINE & LE LABO ===
      engine: null,
      courseData: null,
      workspaceMode: 'investigation', // investigation | experimentation | synthese
      activeAsset: null,

      // === LE "BUFFER" : LE PONT ENTRE SCÉNARIO ET LABO ===
      harvestedData: {
        reflections: {}, // { stepId: "Ma réponse..." }
        extractedConcepts: [], // [ { concept: "Habitus", source: "Bourdieu" } ]
        evidences: [],
        steps: {},
      },
      
      // === ÉTATS D'EXPLORATION (L'Enquête) ===
      activeInvestigation: {
        moduleId: null,
        granuleId: null, // La pièce à conviction / ressource actuelle
        type: null,      // video, pdf, lab-activity, etc.
        discoveredEvidence: new Set(), // Granules consultés
      },

      // === LE PORTEFEUILLE DE PREUVES (Progression) ===
      inventory: {
        completedGranules: new Set(),
        acquiredSkills: [],
        pesaCount: 0,
        certificationThreshold: 70,
        failedActivities: new Set(), 
      },

      // === ÉTAT DES MODULES (Les Territoires) ===
      modules: [], // Piloté par l'Engine
      currentModule: null,
      currentModuleIndex: 0,
      isCertified: false,
      currentStep: null,
      embeddedMedia: null,
      quizResults: [],
      granuleTimings: {},


      // === UI DYNAMIQUE  ===
      ui: {
        sidebarExpanded: true,
        showLabOverlay: false, 
        activePanel: 'explorer', 
        isSyncing: false,
        showNarrator: true,
        engineerDiagramUnlocked: false,
      },

      // === ACTIONS DE HAUT NIVEAU ===
      
      // Initialisation (appelée depuis le Dashboard)
      initParcours: (data, progress) => {
  const engine = new MasterParcoursEngine(data, progress);
  const modules = engine.state.modules;
  const completedGranules = engine.state.completedGranules; // Set<string>

  set({
    engine,
    courseData: data,
    modules,
    currentModuleIndex: engine.state.currentModuleIndex,
    inventory: {
      ...get().inventory,
      pesaCount: progress?.stats?.pointsAccumules || 0,
      acquiredSkills: progress?.rewardsEarned?.badges || []
    }
  });

  // --- Reprise au bon endroit ---
  let targetGranuleId = null;

  const currentModuleId = progress?.currentLocation?.moduleId?.toString();
  let startIndex = modules.findIndex(m => m.id?.toString() === currentModuleId);
  if (startIndex === -1) startIndex = 0;

  // Helper pour tester si un granule est terminé (toutes les variantes d'ID)
  const isGranuleCompleted = (granule) => {
    const idsToCheck = [
      granule.id?.toString(),
      granule._id?.toString(),
      granule._id?.$oid?.toString()
    ].filter(Boolean);
    return idsToCheck.some(id => completedGranules.has(id));
  };

  for (let i = startIndex; i < modules.length; i++) {
    const mod = modules[i];
    const firstPending = mod.granules?.find(g => !isGranuleCompleted(g));
    if (firstPending) {
      targetGranuleId = firstPending.id;
      break;
    }
  }

  if (targetGranuleId) {
    get().focusResource(targetGranuleId);
  } else {
    console.log("✅ Tous les granules sont complétés.");
    // Optionnel : rediriger vers capstone
  }
},

      // === INITIALISATION FLASH POUR LE LAB ===
      initLabPreview: (inputData) => {
        console.log("🧪 Mode Lab activé : Analyse des données reçues");

        // CAS A : Si on reçoit le PARCOURS ENTIER (cas actuel avec localStorage)
        if (inputData.modules) {
          set({ 
            modules: inputData.modules,
            currentParcours: inputData, // On garde tout le parcours
            isPreview: true,
            workspaceMode: 'investigation' 
          });
          return;
        }

        // CAS B : Si on reçoit une ACTIVITÉ SEULE (fallback pour garder la compatibilité)
        const mockModule = {
          id: 'lab-module',
          title: 'Preview Laboratory',
          granules: [{
            id: inputData.id || 'temp-act',
            type: 'activity',
            content: inputData
          }]
        };

        set({ 
          modules: [mockModule],
          currentModule: mockModule,
          activeItem: {
            id: inputData.id || 'temp-act',
            type: 'activity',
            data: inputData
          },
          isPreview: true,
          workspaceMode: 'investigation' 
        });
      },

      unlockEngineerDiagram: async () => {
        try {
          const result = await parcoursService.unlockEngineerDiagram();
          
          if (result.success && result.unlocked) {
            set(state => ({
              ui: {
                ...state.ui,
                engineerDiagramUnlocked: true
              }
            }));
            console.log("Vue diagramme ingénieur débloquée");
            return true;
          }
          
          throw new Error(result.message || "Accès refusé");
        } catch (error) {
          console.error("Échec déblocage vue ingénieur :", error);
          throw error;
        }
      },

      resolveAsset: (path) => {
        const { isPreview } = get();
        if (!path) return "";
        
        // 1. Gestion des URLs absolues et des Blobs (Preview locale)
        if (path.startsWith('http') || path.startsWith('blob:') || path.startsWith('data:')) {
          return path;
        }

        // 2. Mode Preview : On garde le chemin brut (nom du fichier ou blob)
        if (isPreview) {
          return path; 
        }

        // 3. Logique de nettoyage pour le Backend
        // On retire les slashs de début pour normaliser
        let cleanPath = path.replace(/^\/+/, '');

        // Dossiers autorisés à la racine du serveur 
        const rootFolders = ['uploads', 'images', 'audios', 'videos'];
        
        // On vérifie si le chemin commence déjà par un de ces dossiers
        const startsWithKnownFolder = rootFolders.some(folder => cleanPath.startsWith(`${folder}/`));

        if (startsWithKnownFolder) {
          // Si oui (ex: "audios/music.mp3"), on concatène simplement avec l'URL de base
          return `${process.env.REACT_APP_API_URL}/${cleanPath}`;
        } else {
          // Si non (ex: "photo.jpg"), on le range par défaut dans uploads
          return `${process.env.REACT_APP_API_URL}/uploads/${cleanPath}`;
        }
      },

      validateGranule: (granuleId, results = {}) => {
        const state = get();
        const { engine, modules, currentModuleIndex } = state;
        if (!engine) return;

        // 1. Validation dans le moteur (enregistre la complétion, met à jour les points)
        engine.validateGranule(granuleId, results);

        // 2. Mettre à jour l'inventaire
        set({
          inventory: {
            pesaCount: engine.state.pesaCount,
            acquiredSkills: engine.state.acquiredBadges || state.inventory.acquiredSkills,
          },
        });

        // 3. Trouver le granule suivant
        const currentModule = modules[currentModuleIndex];
        const currentGranuleIndex = currentModule?.granules?.findIndex(
          g => g.id?.toString() === granuleId?.toString() || g._id?.toString() === granuleId?.toString()
        );

        let nextGranule = null;
        if (currentGranuleIndex !== undefined && currentGranuleIndex >= 0) {
          if (currentGranuleIndex < currentModule.granules.length - 1) {
            // Granule suivant dans le même module
            nextGranule = currentModule.granules[currentGranuleIndex + 1];
          } else {
            // Dernier granule du module → module suivant
            const nextModule = modules[currentModuleIndex + 1];
            if (nextModule?.granules?.length > 0) {
              nextGranule = nextModule.granules[0];
            }
          }
        } else {
          // Granule introuvable dans le module courant → chercher partout (fallback)
          for (const mod of modules) {
            const g = mod.granules.find(g => g.id?.toString() === granuleId?.toString());
            if (g) {
              const idx = mod.granules.indexOf(g);
              if (idx < mod.granules.length - 1) {
                nextGranule = mod.granules[idx + 1];
              } else {
                // fin du module, chercher le prochain module
                const modIdx = modules.indexOf(mod);
                const nextModule = modules[modIdx + 1];
                if (nextModule?.granules?.length > 0) {
                  nextGranule = nextModule.granules[0];
                }
              }
              break;
            }
          }
        }

        if (nextGranule) {
          get().focusResource(nextGranule.id);
        } else {
          // Parcours terminé
          set({ activeItem: null, currentModule: null });
        }
      },

      syncWithBack: async () => {
        const { engine, courseData, granuleTimings, quizResults } = get();
        if (!engine || !courseData) return;

        try {
            // Récupérer l'état de base de l'Engine
            const snapshot = engine.serialize();

            // Enrichir completedGranules avec timeSpent
            const completedGranules = (snapshot.completedGranules || []).map(granuleId => ({
                granuleId: typeof granuleId === 'string' ? granuleId : granuleId.granuleId,
                timeSpent: granuleTimings[granuleId] || 0,
            }));

            // Ajouter les résultats de quiz accumulés depuis la dernière sync
            const fullSnapshot = {
                ...snapshot,
                completedGranules,
                quizResults: quizResults || [],
            };

            await parcoursService.sync(courseData._id, fullSnapshot);

            // Vider les quizResults après envoi réussi (pour ne pas les renvoyer)
            set({ quizResults: [] });

            console.log("🧬 Synchronisation Engine -> MongoDB réussie");
        } catch (err) {
            console.error("❌ Échec de la synchronisation:", err);
        }
      },       

      focusResource: (granuleId) => {
        const { modules, ui } = get();
        let foundModule = null;
        let foundGranule = null;

        for (const mod of modules) {
          const g = mod.granules?.find(item => 
            item.id === granuleId || 
            item._id === granuleId || 
            item._id?.$oid === granuleId
          );
          if (g) {
            foundModule = mod;
            foundGranule = g;
            break;
          }
        }

        if (foundGranule && foundModule) {
          const isLab = foundGranule.type === 'activity' || foundGranule.type === 'lab-activity';
          const linkedFiche = foundModule.fiches?.find(f => f.id === foundGranule.content?.associatedFicheId);

          set({ 
            currentModule: foundModule,
            activeInvestigation: {
               ...foundGranule,
               linkedFiche // L'UI sait maintenant qu'une ressource est liée à ce moment précis
            },
            activeItem: {
              //id: foundGranule.id,
              id: granuleId,
              type: foundGranule.type,
              data: { ...foundGranule.content, ...foundGranule },
              resources: foundGranule.resources || foundGranule.content?.resources || {}
            },
            ui: { 
              ...ui, 
              activePanel: 'explorer',
              // FORCE l'ouverture du Lab si c'est une activité
              showLabOverlay: isLab 
            }
          });
        }
      },

      openDiagnosticQuiz: (moduleId) => {
        console.log("Hook chargé - openDiagnosticQuiz disponible");
        const { modules } = get();

        const module = modules.find(m => m.id === moduleId);
        if (!module) {
          console.warn("openDiagnosticQuiz : module non trouvé", moduleId);
          return false;
        }

        // Si pas de quiz ou quiz vide → on ne lance rien
        if (!module.diagnosticQuiz || !module.diagnosticQuiz.questions || module.diagnosticQuiz.questions.length === 0) {
          console.log("Pas de diagnosticQuiz valide pour le module", module.title);
          return false;
        }

        console.log("🚀 Lancement du diagnosticQuiz pour", module.title);

        set({
          activeItem: {
            id: `diagnostic-${moduleId}`,
            type: 'diagnostic-quiz',
            data: module.diagnosticQuiz
          }
        });

        return true; // important pour que la sidebar sache que c’est lancé
      },

      openFinalQuiz: (moduleId) => {
        if (!moduleId) {
          console.warn("openFinalQuiz appelé sans ID valide");
          return;
        }

        const { modules } = get();
        
        const module = modules.find(m => m.id === moduleId);  // ← utilise m.id

        if (!module) {
          console.warn("Module non trouvé pour quiz final", moduleId);
          console.log("IDs disponibles :", modules.map(m => m.id));
          return;
        }

        if (!module.finalQuiz) {
          console.warn("finalQuiz manquant pour le module", module.title || module.titre);
          return;
        }

        set({
          activeItem: {
            id: `final-quiz-${module.id}`,
            type: 'final-quiz',
            data: module.finalQuiz
          }
        });
      },

      validateDiagnosticQuiz: async (moduleId, results) => {
        const { engine, courseData } = get();
        if (!engine || !courseData) return;

        console.log("🎯 validateDiagnosticQuiz appelé avec:", { moduleId, results });

        // 1. Validation Engine locale (si nécessaire, adaptez)
        engine.validateDiagnosticQuiz?.(moduleId, results.passed);

        set({
            modules: engine.state.modules,
            currentModuleIndex: engine.state.currentModuleIndex,
            inventory: {
                ...get().inventory,
                pesaCount: engine.state.pesaCount,
                completedGranules: engine.state.completedGranules
            }
        });

        // 2. Payload spécifique au diagnostic
        const pesaGain = Math.round(results.score / 10);
        const timeSpent = results.timeSpent || 0;

        const diagnosticPayload = {
            granuleType: 'quiz',
            granuleId: `diagnostic-${moduleId}`,  
            timeSpent,
            expectedDuration: 600,
            score: results.score || 0,
            passed: results.passed || false,
            quizId: results.quizId || null,
            answers: results.answers || [],
            pesaGain: pesaGain
        };

        console.log("📤 Payload diagnostic:", diagnosticPayload);
        set(state => ({
        quizResults: [
            ...state.quizResults,
            {
                moduleId,
                type: 'diagnostic',
                score: results.score || 0,
                passed: results.passed || false,
                answers: results.answers || [],
                quizId: results.quizId || null,
                timeSpent
            }
          ]
        }));

        try {
            const response = await parcoursService.updateLegacyProgress(
                courseData._id,
                moduleId,
                diagnosticPayload
            );

            if (response.data.success) {
                console.log("🏆 Diagnostic validé en base:", response.data);
            }
        } catch (err) {
            console.error("❌ Échec validation diagnostic:", err.response?.data || err.message);
        }
      },

      validateComprehensionQuiz: async (moduleId, results) => {
        const { engine, courseData, modules } = get();
        if (!engine || !courseData) return;

        // 1. Validation Engine locale
        engine.validatecomprehensionQuiz?.(moduleId, results.passed);

        set({
          modules: engine.state.modules,
          currentModuleIndex: engine.state.currentModuleIndex,
          inventory: {
            ...get().inventory,
            pesaCount: engine.state.pesaCount,
            completedGranules: engine.state.completedGranules
          }
        });

        // 2. Payload et envoi backend (inchangé)
        const pesaGain = Math.round(results.score / 10);
        const timeSpent = results.timeSpent || 0;

        const comprehensionPayload = {
          granuleType: 'quiz',
          granuleId: `comprehension-${moduleId}`,
          timeSpent,
          expectedDuration: 600,
          score: results.score || 0,
          passed: results.passed || false,
          quizId: results.quizId || null,
          answers: results.answers || [],
          pesaGain
        };

        set(state => ({
          quizResults: [
            ...state.quizResults,
            {
              moduleId,
              type: 'comprehension',
              score: results.score || 0,
              passed: results.passed || false,
              answers: results.answers || [],
              quizId: results.quizId || null,
              timeSpent
            }
          ]
        }));

        try {
          const response = await parcoursService.updateLegacyProgress(
            courseData._id,
            moduleId,
            comprehensionPayload
          );
          if (response.data.success) {
            console.log("🏆 comprehension validé en base:", response.data);
          }
        } catch (err) {
          console.error("❌ Échec validation comprehension:", err.response?.data || err.message);
        }

        // 3. Navigation vers le prochain granule (NOUVEAU)
        const currentMod = modules.find(m => m._id === moduleId || m.id === moduleId);
        if (!currentMod) return;

        // Chercher l'index du granule quiz dans le module
        const quizGranuleId = `comprehension-${moduleId}`;
        const currentGranuleIndex = currentMod.granules.findIndex(g =>
          g.id === quizGranuleId || g._id === quizGranuleId
        );

        let nextGranule = null;

        if (currentGranuleIndex !== -1 && currentGranuleIndex < currentMod.granules.length - 1) {
          // Il y a un granule suivant dans le même module
          nextGranule = currentMod.granules[currentGranuleIndex + 1];
        } else {
          // Fin du module → passer au module suivant
          const currentModIndex = modules.findIndex(m => m._id === moduleId || m.id === moduleId);
          const nextModule = modules[currentModIndex + 1];
          if (nextModule?.granules?.length > 0) {
            nextGranule = nextModule.granules[0];
          }
        }

        if (nextGranule) {
          get().focusResource(nextGranule.id);
        } else {
          // Plus rien : fin du parcours
          set({ activeItem: null, currentModule: null });
          // Éventuellement rediriger vers le capstone ou une page de félicitations
        }
      },

      validateFinalQuiz: async (moduleId, results) => {
        const { engine, courseData } = get();
        if (!engine || !courseData) return;        

        // 1. Validation Engine locale
        engine.validateFinalQuiz(moduleId, results.passed);

        set({
          modules: engine.state.modules,
          currentModuleIndex: engine.state.currentModuleIndex,
          inventory: {
            ...get().inventory,
            pesaCount: engine.state.pesaCount,
            completedGranules: engine.state.completedGranules
          }
        });

        // 2. ✅ PAYLOAD CORRIGÉ
        const pesaGain = Math.round(results.score / 10);
        const timeSpent = results.timeSpent || 0;
        
        const finalQuizPayload = {
          granuleType: 'quiz', 
          granuleId: `final-quiz-${moduleId}`,
          timeSpent,
          expectedDuration: 600, 
          
          score: results.score || 0,
          passed: results.passed || false,
          quizId: results.quizId || null,
          answers: results.answers || [],
          pesaGain: pesaGain
        };
        
        set(state => ({
        quizResults: [
            ...state.quizResults,
            {
                moduleId,
                type: 'final',
                score: results.score || 0,
                passed: results.passed || false,
                answers: results.answers || [],
                quizId: results.quizId || null,
                timeSpent
            }
            ]
        }));

        try {
          const response = await parcoursService.updateLegacyProgress( 
            courseData._id,
            moduleId,
            finalQuizPayload
          );

          if (response.data.success) {
            console.log("🏆 Quiz Final validé en base:", response.data);
            
            if (response.data.certificateGenerated) {
              console.log("🎓 Certificat généré !");
            }
          }
        } catch (err) {
          console.error("❌ Échec validation backend:", err.response?.data || err.message);
        }
      },

      // 2. Harvesting : La fonction qui crée la valeur
      harvestStepData: (stepId, value) => {
        set(state => {
          const richPayload = typeof value === 'string'
            ? { type: 'text', text: value, harvestedAt: new Date().toISOString() }
            : { ...value, harvestedAt: new Date().toISOString() };

          return {
            harvestedData: {
              ...state.harvestedData,
              reflections: {
                ...state.harvestedData.reflections,
                [stepId]: richPayload,          // ← payload riche (comme avant), pas juste le texte
              },
              steps: {
                ...(state.harvestedData.steps || {}),
                [stepId]: richPayload,          // ← idem, pour transmission backend
              },
            },
          };
        });
      },

      // 3. Validation augmentée : Transférer les résultats vers le Labo
      validateEvidence: async (granuleId, results) => {
        const { engine, courseData, currentModule, modules, currentModuleIndex, harvestedData } = get();
        if (!engine) return;

        // Mise à jour moteur
        engine.validateGranule(granuleId, { ...results, harvestedData });

        // Résolution du module
        const resolvedModule =
          currentModule ||
          modules.find(m =>
            m.granules?.some(g =>
              String(g.id) === String(granuleId) ||
              String(g._id) === String(granuleId)
            )
          ) ||
          modules[currentModuleIndex] ||
          modules[0];

        if (!resolvedModule) {
          console.error("❌ validateEvidence : aucun module résolvable pour", granuleId);
          set({
            inventory: {
              ...get().inventory,
              pesaCount: engine.state.pesaCount,
              completedGranules: engine.state.completedGranules,
            },
          });
          return;
        }

        // ✅ Normalisation du type
        const rawType = results.type || 'activity';
        let normalizedType;
        if (rawType === 'video' || rawType === 'scenario') normalizedType = 'theory';
        else if (rawType === 'activity') normalizedType = 'activity';
        else if (rawType === 'quiz') normalizedType = 'quiz';
        else normalizedType = 'activity'; // fallback

        const timeSpent = results.timeSpent || 0;

        // Stocker le temps pour sync ultérieur
        set(state => ({
          granuleTimings: {
            ...state.granuleTimings,
            [granuleId]: timeSpent,
          },
        }));

        const legacyPayload = {
          granuleType: normalizedType,
          granuleId,
          timeSpent,
          expectedDuration: results.expectedDuration || 600,
          score:            results.score || 0,
          passed:           results.passed || false,
          quizId:           results.quizId || null,
          answers:          results.answers || [],
          harvest:          harvestedData,
        };

        // Sync inventaire local immédiatement
        set({
          inventory: {
            ...get().inventory,
            pesaCount:         engine.state.pesaCount,
            completedGranules: engine.state.completedGranules,
            harvestedContent:  engine.harvestedData?.steps,
          },
        });

        // Sync réseau
        try {
          await parcoursService.updateLegacyProgress(
            courseData._id,
            resolvedModule.id,
            legacyPayload
          );
          console.log("✅ validateEvidence synced to backend for module:", resolvedModule.id);
        } catch (err) {
          console.error("❌ Sync error (validateEvidence):", err?.response?.data || err.message);
        }
      },

      // 4. Open Asset : Avec gestion des "Capabilities"
      openAsset: (assetId) => {
        const { courseData, modules, currentModule } = get();
        let found = null;

        // 1. Recherche prioritaire dans le module courant
        const current = currentModule || {};

        // a) Dans fiches normales
        found = current.fiches?.find(f => 
          String(f.id || f._id) === String(assetId)
        );

        // b) Dans bonusFiches (si pas trouvé avant)
        if (!found) {
          found = current.bonusFiches?.find(f => 
            String(f.id || f._id) === String(assetId)
          );
        }

        // 2. Fallback : recherche dans tous les modules
        if (!found) {
          for (const mod of (courseData?.modules || modules || [])) {
            // fiches
            found = mod.fiches?.find(f => 
              String(f.id || f._id) === String(assetId)
            );
            if (found) break;

            // bonusFiches
            if (!found) {
              found = mod.bonusFiches?.find(f => 
                String(f.id || f._id) === String(assetId)
              );
              if (found) break;
            }
          }
        }

        // 3. Si trouvé → on enrichit et on active
        if (found) {
          const enriched = {
            ...found,
            isConceptTool: found.isConceptTool || false,
            capabilities: found.capabilities || [],
            isBonus: !!found.category?.includes?.('bonus') || found.titre?.toLowerCase().includes('bonus'), // détection bonus
            filePath: found.filePath || found.file || null, // normalisation
            content: found.content || null,
            titre: found.titre || found.nom || 'Sans titre'
          };

          console.log("Asset trouvé et activé :", {
            id: assetId,
            titre: enriched.titre,
            isBonus: enriched.isBonus,
            filePath: enriched.filePath,
            hasContent: !!enriched.content
          });

          set({ activeAsset: enriched });
        } else {
          console.warn("Aucun asset trouvé pour ID :", assetId);
        }
      },

      selectModuleById: (moduleId) => {
        const { modules } = get();
        const target = modules.find(m => m.id === moduleId);
        if (target) {
          set({ 
            currentModule: target,
            currentModuleIndex: modules.indexOf(target)
          });
        }
      },

      getSimulationForModule: (moduleId) => {
        const module = get().modules.find(m => m.id === moduleId);
        return module?.cases || [];
      },
      
      setWorkspaceMode: (mode) => set({ workspaceMode: mode }),
      setCurrentStep: (step) => set({ currentStep: step }),
      setEmbeddedMedia: (media) => set({ embeddedMedia: media }),
      clearEmbeddedMedia: () => set({ embeddedMedia: null }),
      closeAsset: () => set({ activeAsset: null }),

      closeActiveItem: () => {
        set({
          activeItem:          null,
          activeInvestigation: { moduleId: null, granuleId: null, type: null, discoveredEvidence: new Set() },
          embeddedMedia:       null,
          currentStep:         null,
        });
      },

      toggleShowNarrator: () => set((state) => ({ 
        ui: { ...state.ui, showNarrator: !state.ui.showNarrator } 
      })),

      closeNarrator: () => set((state) => ({ 
        ui: { ...state.ui, showNarrator: false } 
      })),

      markActivityFailed: (activityId) => {
        set(state => ({
          inventory: {
            ...state.inventory,
            failedActivities: new Set([...state.inventory.failedActivities, activityId])
          }
        }));
      },

      clearActivityFailed: (activityId) => {
        set(state => {
          const newFailed = new Set(state.inventory.failedActivities);
          newFailed.delete(activityId);
          return {
            inventory: {
              ...state.inventory,
              failedActivities: newFailed
            }
          };
        });
      },

      hasFailedActivity: (activityId) => {
        const { inventory } = get();
        return inventory.failedActivities.has(activityId);
      },

      togglePanel: (panelName) => set(state => ({
        ui: { ...state.ui, activePanel: panelName }
      })),

      // === ACTIONS UI POUR LE CAPSTONE ===

      // Bascule l'état de la sidebar
      setSidebarExpanded: (expanded) => set(state => ({
        ui: { ...state.ui, sidebarExpanded: expanded }
      })),

      // Mode "Focus" : Ferme tout le superflu pour la War Room
      setCapstoneFocus: (active) => {
        set(state => ({
          ui: { 
            ...state.ui, 
            sidebarExpanded: !active, // false quand on est en capstone
            activePanel: active ? 'none' : 'explorer', 
            showNarrator: active ? true : state.ui.showNarrator
          }
        }));
      },

      setVideoScreenFocus: (active) => {
        set(state => ({
          ui: { 
            ...state.ui, 
            sidebarExpanded: !active, // false quand on est en video-intro
            activePanel: active ? 'none' : 'explorer', 
            showNarrator: active ? true : state.ui.showNarrator
          }
        }));
      },

      // Déclencheur intelligent pour le Narrateur (Mediator)
      triggerMediator: (mediatorData) => {
        if (!mediatorData) return;
        
        // On s'assure que le narrateur est visible et on peut imaginer 
        // passer les données au composant Narrator via le store si besoin
        set(state => ({
          ui: { ...state.ui, showNarrator: true }
        }));
        
        console.log("🎙️ Médiateur activé :", mediatorData.description);
      },

      // Dans usePesaParcours.js
      resetLayout: () => {
        set(state => ({
          ui: {
            ...state.ui,
            sidebarExpanded: true,     // Relance l'animation d'ouverture
            activePanel: 'resources',  // Réactive le panneau de droite
            showNarrator: false        // Optionnel : ferme le narrateur
          }
        }));
      }

    };
  }, { name: 'PESA-PARCOURS-V1' })
);